import { useIsFocused } from "@react-navigation/native";
import AutoRenewalConsentSection from "components/AutoRenewalConsentSection";
import Button from "components/Button";
import Chevron from "components/Chevron";
import FullScreenSpinner from "components/FullScreenSpinner";
import HiddenView from "components/HiddenView";
import MainContainer from "components/MainContainer";
import {
  StripeTokenError,
  StripeTokenResponse,
} from "components/StripePaymentForm/StripePaymentForm";
import TenantLogo from "components/TenantLogo";
import {
  createAccount,
  fetchPlanPackages,
  fetchSignupCharge,
  setPrimaryUser,
  setSelectedBillingInterval,
  setShouldCharge,
  setStep,
  uploadUserImages,
} from "features/slices/buyFlowSlice";
import { PrimaryUser } from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useLogout } from "hooks/useLogout";
import usePlanPackageUserFields from "hooks/usePlanPackageUserFields";
import useSendConversionData from "hooks/useSendConversionData";
import useTenantUserFields from "hooks/useTenantUserFields";
import { AccountSetupScreenProps } from "navigation/types";
import i18n from "plugins/localization";
import { useFetchAgreementsQuery } from "queries/agreement.queries";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import { useFetchCurrentUserQuery } from "queries/user.queries";
import React, { useEffect, useState } from "react";
import { Pressable, View } from "react-native";
import {
  calculateSecondardUsersPlanQuantity,
  planPackageIdArray,
} from "screens/Register/registerUtils";
import StepThree from "screens/Register/StepThree";
import StepTwo from "screens/Register/StepTwo";
import { useDebouncedCallback } from "use-debounce";
import { getPlanType } from "utils/planPackageUtils";
import { filterIneligibleSubscriptions } from "utils/subscriptionUtils";
import toast from "utils/toast";
import StepOne from "./StepOne";

const AccountSetup = ({ navigation }: AccountSetupScreenProps) => {
  const dispatch = useAppDispatch();
  const { account, fetchAccount } = useAccount();
  const isFocused = useIsFocused();
  const {
    step,
    selectedPlanPackage,
    selectedBillingInterval,
    primaryUser,
    secondaryUsers,
    loading,
    signupCharge,
  } = useAppSelector((state) => state.buyFlow);
  const { tenantUserFields } = useTenantUserFields();
  const { planPackageUserFields } = usePlanPackageUserFields(
    selectedPlanPackage?.id,
  );
  const sendConversionData = useSendConversionData();

  const storedUserId = useAppSelector((state) => state.auth.id);
  const { data: userResponse } = useFetchCurrentUserQuery(storedUserId);
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const { data: tenantBranding } = useFetchTenantBrandingQuery();
  const { data: autoRenewalAgreements } = useFetchAgreementsQuery(
    "AUTO_RENEWAL",
    selectedPlanPackage?.id,
    selectedBillingInterval,
  );
  const [autoRenewalConsentGiven, setAutoRenewalConsentGiven] = useState(false);
  const isAutoRenewPlan =
    selectedPlanPackage?.requireAutoRenewalConsent === true;

  const { tenantSubdomain, tenantType } = useAppSelector(
    (state) => state.tenant,
  );
  const isBankType = selectedPlanPackage
    ? getPlanType(selectedPlanPackage) === "BANK"
    : false;
  const { logoutAccount } = useLogout();
  if (!tenantType || !tenantSubdomain) {
    return null;
  }

  const quantity =
    primaryUser?.quantity! +
    calculateSecondardUsersPlanQuantity(secondaryUsers);

  const refetchSignupCharge = useDebouncedCallback(
    (selectedPlanPackage) =>
      dispatch(
        fetchSignupCharge({
          planPackageIds: planPackageIdArray(quantity, selectedPlanPackage.id),
          billingInterval: selectedBillingInterval,
        }),
      ),
    250,
  );
  const handleTokenReceived = async (
    error: StripeTokenError | undefined,
    response: StripeTokenResponse | undefined,
  ) => {
    if (error) {
      toast.error(error.error.message);
      return;
    }
    await dispatch(uploadUserImages());
    const result = await dispatch(
      createAccount({
        billingToken: response!,
        tenantUserFields: tenantUserFields!,
        planPackageUserFields: planPackageUserFields!,
        isRegistering: false,
        emailMarketingConsent: false,
        smsMarketingConsent: false,
        autoRenewalAgreementId:
          isAutoRenewPlan && autoRenewalConsentGiven
            ? autoRenewalAgreements?.[0]?.id
            : undefined,
      }),
    );
    if (result.meta.requestStatus !== "rejected") {
      sendConversionData(signupCharge, quantity, response!);
      fetchAccount();
      navigation.navigate("MainFlow", {
        screen: "Home",
        params: {
          screen: "DashboardScreen",
        },
      });
    }
  };

  const handleBackButtonPress = () => {
    if (isBankType && step === 3) {
      dispatch(setStep(1));
      return;
    }
    dispatch(setStep(step - 1));
  };

  useEffect(() => {
    if (!tenantConfig) return;
    if (tenantConfig.disableBuyflow) {
      window.location.replace(tenantConfig.buyflowRedirectUrl);
    }
  }, [tenantConfig]);

  useEffect(() => {
    async function init() {
      if (!userResponse) return;
      dispatch(fetchPlanPackages(null));
      const user = { ...userResponse, quantity: 1 };
      dispatch(setPrimaryUser(user as PrimaryUser));
    }
    init();
  }, [userResponse]);

  useEffect(() => {
    if (!selectedPlanPackage) return;
    if (Number(selectedPlanPackage.fullPrice) === 0) {
      dispatch(setShouldCharge(false));
    } else {
      dispatch(setShouldCharge(true));
    }
    refetchSignupCharge(selectedPlanPackage);
  }, [selectedPlanPackage, selectedBillingInterval, quantity]);

  useEffect(() => {
    if (!selectedPlanPackage) return;
    dispatch(
      setSelectedBillingInterval(selectedPlanPackage?.billingIntervals[0]),
    );
    setAutoRenewalConsentGiven(false);
  }, [selectedPlanPackage]);

  useEffect(() => {
    if (!account || !isFocused) return;
    const accountHasValidSubscription = account.subscriptions.some((sub) =>
      filterIneligibleSubscriptions(sub),
    );
    if (accountHasValidSubscription) {
      navigation.navigate("MainFlow", {
        screen: "Home",
        params: { screen: "DashboardScreen" },
      });
    }
  }, [account, isFocused]);

  return (
    <MainContainer showHeader={false}>
      {loading && <FullScreenSpinner />}
      <View
        style={{
          padding: 15,
        }}
      >
        <View style={{ alignItems: "center", marginBottom: 15 }}>
          <TenantLogo isDarkBackground={false} />
        </View>

        {step !== 1 && (
          <Pressable onPress={handleBackButtonPress}>
            <Chevron direction="left"></Chevron>
          </Pressable>
        )}

        <HiddenView shouldHide={step !== 1}>
          <StepOne
            onContinuePress={() => handleTokenReceived(undefined, undefined)}
          ></StepOne>
        </HiddenView>

        <HiddenView shouldHide={step !== 2}>
          <StepTwo
            selectedBillingInterval={selectedBillingInterval}
            selectedPlanPackage={selectedPlanPackage}
            onContinuePress={() => handleTokenReceived(undefined, undefined)}
          ></StepTwo>
        </HiddenView>

        <HiddenView shouldHide={step !== 3}>
          {selectedPlanPackage && (
            <StepThree
              selectedBillingInterval={selectedBillingInterval}
              selectedPlanPackage={selectedPlanPackage}
              onTokenReceived={handleTokenReceived}
              disabled={
                isAutoRenewPlan &&
                autoRenewalAgreements &&
                autoRenewalAgreements.length > 0 &&
                !autoRenewalConsentGiven
              }
              beforeButton={
                isAutoRenewPlan ? (
                  <AutoRenewalConsentSection
                    agreements={autoRenewalAgreements}
                    consentGiven={autoRenewalConsentGiven}
                    onChange={setAutoRenewalConsentGiven}
                  />
                ) : undefined
              }
            ></StepThree>
          )}
        </HiddenView>

        <Button
          style={{ marginTop: 15 }}
          appearance="ghost"
          onPress={logoutAccount}
        >
          {i18n.t("screens.AccountSetup.logOutHere")}
        </Button>
      </View>
    </MainContainer>
  );
};

export default AccountSetup;
